/**
 * Normalize Craft.js config from API / storage into a value Frame accepts as `data`.
 * Accepts JSON string or already-parsed object.
 */
export const normalizeEditorConfig = (config) => {
  if (!config) return null;

  if (typeof config === 'string') {
    const trimmed = config.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return trimmed;
    } catch {
      return null;
    }
  }

  if (typeof config === 'object' && !Array.isArray(config)) {
    try {
      return JSON.stringify(config);
    } catch {
      return null;
    }
  }

  return null;
};

const hasResolvableType = (node, resolver) => {
  if (!node || typeof node !== 'object') return false;

  const resolvedName = node.type?.resolvedName;
  if (!resolvedName || typeof resolvedName !== 'string') return false;

  return Boolean(resolver[resolvedName]);
};

const hasValidNodeReferences = (parsed) => {
  return Object.values(parsed).every((node) => {
    const childIds = Array.isArray(node?.nodes) ? node.nodes : [];
    if (!childIds.every((childId) => parsed[childId])) return false;

    const linkedIds = Object.values(node?.linkedNodes || {});
    return linkedIds.every((linkedId) => parsed[linkedId]);
  });
};

/**
 * True when config can safely hydrate a Craft.js Frame (has ROOT + resolvable nodes).
 */
export const isConfigRenderable = (config, resolver = {}) => {
  const normalized = normalizeEditorConfig(config);
  if (!normalized) return false;

  let parsed;
  try {
    parsed = JSON.parse(normalized);
  } catch {
    return false;
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  if (!parsed.ROOT || typeof parsed.ROOT !== 'object') return false;

  if (!Object.values(parsed).every((node) => hasResolvableType(node, resolver))) {
    return false;
  }

  return hasValidNodeReferences(parsed);
};

/**
 * Prefer quote snapshot; fall back to template config when snapshot is invalid.
 */
export const resolveQuoteConfig = (snapshot, templateConfig, resolver = {}) => {
  if (isConfigRenderable(snapshot, resolver)) {
    return normalizeEditorConfig(snapshot);
  }
  if (isConfigRenderable(templateConfig, resolver)) {
    return normalizeEditorConfig(templateConfig);
  }
  return null;
};

export default {
  normalizeEditorConfig,
  isConfigRenderable,
  resolveQuoteConfig
};
